/**
 * ContinuityPostureView (P367) -- the continuity-posture register
 * (MVB gap-analysis F3). Route: /twin/continuity-posture.
 *
 * Per MVB-supporting service: what continuity capability exists,
 * whether it can meet the tightest supported tolerance, and when it
 * was last proven (declared or by a passed drill).
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  continuityPostureLabel,
  postureVerdictLabel,
} from "shared/vocab/workflowState";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";

interface PostureRowOut {
  service_id: string;
  name: string;
  posture: string | null;
  rto_hours: number | null;
  tightest_tolerance_hours: number | null;
  supported_mvb_ibs: string[];
  last_proven_on: string | null;
  proof_source: "declared" | "drill" | null;
  proof_drill_id: string | null;
  proof_drill_name: string | null;
  evidence_ref: string | null;
  tightest_horizon_hours: number | null;
  meets_horizon: boolean | null;
  verdict: "no_posture" | "insufficient" | "unproven" | "ok";
  reason: string;
}

interface PostureReportOut {
  generated_at: string;
  summary: {
    total: number;
    ok: number;
    unproven: number;
    insufficient: number;
    no_posture: number;
  };
  rows: PostureRowOut[];
}

const VERDICT_CLASS: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-800",
  unproven: "bg-amber-100 text-amber-800",
  insufficient: "bg-rose-100 text-rose-800",
  no_posture: "bg-rose-100 text-rose-800",
};

/* Round 11 F3 -- the words moved to the catalogue (`vocab
   .continuityPosture`), where `markUntranslated` can see them and a
   second reader can share them. This map was hard-coded English in one
   view and the disposition page was printing the raw column value
   beside it. */

export function ContinuityPostureView() {
  const { t } = useTranslation();
  const report = useQuery({
    queryKey: ["twin", "continuity-posture"],
    queryFn: () => api.get<PostureReportOut>("/v1/twin/mvb/continuity-posture"),
  });
  const p = report.data;

  return (
    <article data-testid="continuity-posture-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="mvb" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("continuityPosture.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("continuityPosture.body")}
        </p>
      </header>

      {report.isError && (
        <p className="text-sm text-rose-700">{t("continuityPosture.error")}</p>
      )}

      {/* Round 19 H56 -- with nothing declared the page read "0
          MVB-supporting service(s) / 0 ok / 0 unproven / ..." over an empty
          table, with no sentence and no next step. */}
      {p && p.rows.length === 0 && (
        <p data-testid="continuity-posture-empty" className="text-sm text-slate-700">
          {t("continuityPosture.empty")}{" "}
          <Link to="/foundation/mvb" className="text-brand-brassDark hover:underline">
            {t("continuityPosture.emptyLink")}
          </Link>
        </p>
      )}

      {p && p.rows.length > 0 && (
        <>
          <section
            data-testid="continuity-posture-summary"
            className="flex flex-wrap gap-2 text-sm"
          >
            <span className="rounded border border-paper-2 bg-white px-2 py-1">
              {t("continuityPosture.total", { count: p.summary.total })}
            </span>
            <span className={`rounded px-2 py-1 ${countTone(p.summary.ok, "ok")}`}>
              {t("continuityPosture.countOk", { count: p.summary.ok })}
            </span>
            <span
              className={`rounded px-2 py-1 ${countTone(p.summary.unproven, "unproven")}`}
            >
              {t("continuityPosture.countUnproven", { count: p.summary.unproven })}
            </span>
            <span
              className={`rounded px-2 py-1 ${countTone(p.summary.insufficient, "bad")}`}
            >
              {t("continuityPosture.countInsufficient", {
                count: p.summary.insufficient,
              })}
            </span>
            <span
              className={`rounded px-2 py-1 ${countTone(p.summary.no_posture, "bad")}`}
            >
              {t("continuityPosture.countNoPosture", { count: p.summary.no_posture })}
            </span>
          </section>

          <table
            data-testid="continuity-posture-table"
            className="w-full border-collapse text-sm"
          >
            <thead>
              <tr className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-1 text-start">{t("continuityPosture.service")}</th>
                <th className="text-start">{t("continuityPosture.posture")}</th>
                <th className="text-end">{t("continuityPosture.rto")}</th>
                <th className="text-end">{t("continuityPosture.tolerance")}</th>
                <th className="text-start">{t("continuityPosture.lastProven")}</th>
                <th className="text-end">{t("continuityPosture.horizon")}</th>
                <th className="text-end">{t("continuityPosture.verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {p.rows.map((r) => (
                <tr key={r.service_id} className="border-t border-paper-2 align-top">
                  <td className="py-1.5">
                    <span className="font-medium text-slate-900">{r.name}</span>
                    <span
                      className="block text-xs text-slate-400"
                      title={r.supported_mvb_ibs.join(", ")}
                    >
                      {t("continuityPosture.supports", {
                        count: r.supported_mvb_ibs.length,
                      })}
                    </span>
                  </td>
                  <td>
                    {r.posture ? (
                      continuityPostureLabel(r.posture)
                    ) : (
                      <span className="italic text-slate-400">—</span>
                    )}
                  </td>
                  <td className="text-end font-mono tabular-nums">
                    {r.rto_hours !== null ? `${r.rto_hours}h` : "—"}
                  </td>
                  <td className="text-end font-mono tabular-nums">
                    {r.tightest_tolerance_hours !== null
                      ? `${r.tightest_tolerance_hours}h`
                      : "—"}
                  </td>
                  <td>
                    {r.last_proven_on ? (
                      <>
                        <span className="font-mono text-xs">{r.last_proven_on}</span>{" "}
                        {r.proof_source === "drill" ? (
                          r.proof_drill_id ? (
                            <Link
                              to="/twin/drills"
                              className="text-xs text-brand-brassDark hover:underline"
                              title={r.proof_drill_name ?? undefined}
                            >
                              ({t("continuityPosture.viaDrill")})
                            </Link>
                          ) : (
                            <span className="text-xs text-slate-400">
                              ({t("continuityPosture.viaDrill")})
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-slate-400">
                            ({t("continuityPosture.declared")}
                            {r.evidence_ref ? `: ${r.evidence_ref}` : ""})
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="italic text-slate-400">
                        {t("continuityPosture.never")}
                      </span>
                    )}
                  </td>
                  <td className="text-end font-mono text-xs tabular-nums">
                    {typeof r.tightest_horizon_hours !== "number" ? (
                      <span className="italic text-slate-400">—</span>
                    ) : (
                      <span
                        title={
                          r.meets_horizon === false
                            ? t("continuityPosture.horizonMissedHint")
                            : t("continuityPosture.horizonMetHint")
                        }
                        className={
                          r.meets_horizon === false ? "text-rag-red" : "text-slate-500"
                        }
                      >
                        {/* D4 -- "T", a number and "h" were three
                            separate renders glued together, so
                            right-to-left reordered them into
                            "T+ 24h" with the parts on the wrong sides.
                            One interpolated string, one isolate. */}
                        {r.meets_horizon === false ? "✗" : "✓"}{" "}
                        {t("continuityPosture.horizonChip", {
                          hours: r.tightest_horizon_hours,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="text-end">
                    <span
                      title={r.reason}
                      className={`rounded px-1.5 py-0.5 text-xs font-semibold ${VERDICT_CLASS[r.verdict]}`}
                    >
                      {postureVerdictLabel(r.verdict)}
                    </span>
                    {/* Round 9 F1 -- a verdict a reader can act on.
                        "insufficient" states that a service cannot come
                        back fast enough for the tolerance somebody has
                        signed, and then stops. The two things that
                        close it -- proving the posture in a drill, or
                        raising the gap as work -- were both one route
                        away with nothing pointing at either. A register
                        that names a problem and offers no next move is
                        a list of complaints. */}
                    {(r.verdict === "insufficient" || r.verdict === "no_posture") && (
                      <span className="mt-1 block text-[11px]">
                        <Link
                          to="/twin/drills"
                          data-testid={`continuity-posture-prove-${r.service_id}`}
                          className="text-brand-brassDark hover:underline"
                        >
                          {t("continuityPosture.prove")}
                        </Link>
                        <span className="text-ink-2">{" · "}</span>
                        <Link
                          to={`/twin/findings/new?entity_kind=service&entity_id=${r.service_id}`}
                          data-testid={`continuity-posture-raise-${r.service_id}`}
                          className="text-brand-brassDark hover:underline"
                        >
                          {t("continuityPosture.raise")}
                        </Link>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-xs text-slate-500">{t("continuityPosture.note")}</p>
        </>
      )}
    </article>
  );
}

/** Round 17 B11 -- a count takes its colour only when there is
 *  something in it: "0 ok" was green on an estate with nothing judged. */
function countTone(count: number, kind: "ok" | "unproven" | "bad"): string {
  if (count === 0) return "border border-paper-2 bg-white text-slate-600";
  if (kind === "ok") return "bg-emerald-100 text-emerald-800";
  if (kind === "unproven") return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-800";
}
